export const RENDEZVOUS_VERSION = 1 as const;

export type RendezvousRole = "host" | "join";

export interface RendezvousCredentials {
  readonly roomId: string;
  readonly secret: string;
}

export interface EncryptedPayload {
  readonly v: typeof RENDEZVOUS_VERSION;
  readonly iv: string;
  readonly data: string;
}

export type ClientFrame =
  | { readonly v: 1; readonly type: "join"; readonly roomId: string; readonly role: RendezvousRole; readonly resumeToken?: string }
  | { readonly v: 1; readonly type: "relay"; readonly payload: EncryptedPayload }
  | { readonly v: 1; readonly type: "handoff" };

export type ServerErrorCode =
  | "invalid-message"
  | "invalid-resume"
  | "peer-unavailable"
  | "room-full"
  | "room-not-found";

export type ServerFrame =
  | { readonly v: 1; readonly type: "ready"; readonly sessionId: string; readonly resumeToken: string; readonly resumed: boolean; readonly peerPresent: boolean }
  | { readonly v: 1; readonly type: "peer-joined"; readonly sessionId: string }
  | { readonly v: 1; readonly type: "peer-left"; readonly sessionId: string; readonly reconnectWithinMs: number }
  | { readonly v: 1; readonly type: "relay"; readonly payload: EncryptedPayload }
  | { readonly v: 1; readonly type: "error"; readonly code: ServerErrorCode };

const roomIdPattern = /^[A-Za-z0-9_-]{20,64}$/u;
const encoder = new TextEncoder();
const decoder = new TextDecoder("utf-8", { fatal: true });

export function isRoomId(value: unknown): value is string {
  return typeof value === "string" && roomIdPattern.test(value);
}

export function randomBase64Url(byteLength: number): string {
  return bytesToBase64Url(crypto.getRandomValues(new Uint8Array(byteLength)));
}

export function createRendezvousCredentials(): RendezvousCredentials {
  return { roomId: randomBase64Url(18), secret: randomBase64Url(32) };
}

export function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
}

export function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  if (!/^[A-Za-z0-9_-]+$/u.test(value)) throw new TypeError("Invalid base64url value");
  const padded = value.replaceAll("-", "+").replaceAll("_", "/").padEnd(Math.ceil(value.length / 4) * 4, "=");
  let binary: string;
  try { binary = atob(padded); } catch (error) { throw new TypeError("Invalid base64url value", { cause: error }); }
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

export function isEncryptedPayload(value: unknown): value is EncryptedPayload {
  if (!value || typeof value !== "object") return false;
  const payload = value as Partial<EncryptedPayload>;
  return payload.v === RENDEZVOUS_VERSION && typeof payload.iv === "string" && typeof payload.data === "string";
}

export function parseClientFrame(raw: string): ClientFrame {
  const value = parseObject(raw);
  if (value.v !== RENDEZVOUS_VERSION || typeof value.type !== "string") throw new TypeError("Invalid rendezvous frame");
  if (value.type === "join" && isRoomId(value.roomId) && (value.role === "host" || value.role === "join") &&
    (value.resumeToken === undefined || typeof value.resumeToken === "string")) return value as unknown as ClientFrame;
  if (value.type === "relay" && isEncryptedPayload(value.payload)) return value as unknown as ClientFrame;
  if (value.type === "handoff") return { v: 1, type: "handoff" };
  throw new TypeError("Invalid rendezvous frame");
}

export function parseServerFrame(raw: string): ServerFrame {
  const value = parseObject(raw);
  if (value.v !== RENDEZVOUS_VERSION || typeof value.type !== "string") throw new TypeError("Invalid rendezvous frame");
  if (value.type === "ready" && typeof value.sessionId === "string" && typeof value.resumeToken === "string" &&
    typeof value.resumed === "boolean" && typeof value.peerPresent === "boolean") return value as unknown as ServerFrame;
  if (value.type === "peer-joined" && typeof value.sessionId === "string") return value as unknown as ServerFrame;
  if (value.type === "peer-left" && typeof value.sessionId === "string" && Number.isSafeInteger(value.reconnectWithinMs)) return value as unknown as ServerFrame;
  if (value.type === "relay" && isEncryptedPayload(value.payload)) return value as unknown as ServerFrame;
  if (value.type === "error" && typeof value.code === "string") return value as unknown as ServerFrame;
  throw new TypeError("Invalid rendezvous frame");
}

export class RendezvousCipher {
  readonly #roomId: string;
  readonly #key: Promise<CryptoKey>;
  readonly #seen = new Set<string>();

  constructor(roomId: string, secret: string | Uint8Array) {
    if (!isRoomId(roomId)) throw new TypeError("Invalid rendezvous room ID");
    this.#roomId = roomId;
    this.#key = deriveKey(typeof secret === "string" ? base64UrlToBytes(secret) : Uint8Array.from(secret), roomId);
  }

  async encrypt(value: unknown): Promise<EncryptedPayload> {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const data = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv, additionalData: aad(this.#roomId) },
      await this.#key,
      encoder.encode(JSON.stringify(value)),
    );
    return { v: 1, iv: bytesToBase64Url(iv), data: bytesToBase64Url(new Uint8Array(data)) };
  }

  async decrypt(payload: EncryptedPayload): Promise<unknown> {
    if (!isEncryptedPayload(payload) || this.#seen.has(payload.iv)) throw new TypeError("Invalid or replayed rendezvous payload");
    const iv = Uint8Array.from(base64UrlToBytes(payload.iv));
    if (iv.byteLength !== 12) throw new TypeError("Invalid rendezvous nonce");
    this.#seen.add(payload.iv);
    if (this.#seen.size > 2_048) this.#seen.delete(this.#seen.values().next().value!);
    const clear = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv, additionalData: aad(this.#roomId) },
      await this.#key,
      Uint8Array.from(base64UrlToBytes(payload.data)),
    );
    return JSON.parse(decoder.decode(clear));
  }
}

async function deriveKey(secret: Uint8Array<ArrayBuffer>, roomId: string): Promise<CryptoKey> {
  if (secret.byteLength !== 32) throw new TypeError("Rendezvous secret must contain 32 bytes");
  const source = await crypto.subtle.importKey("raw", secret, "HKDF", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt: encoder.encode(roomId), info: encoder.encode("kitae-rendezvous/signaling/v1") },
    source,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

function aad(roomId: string): Uint8Array<ArrayBuffer> {
  return Uint8Array.from(encoder.encode(`kitae-rendezvous:${RENDEZVOUS_VERSION}:${roomId}`));
}

function parseObject(raw: string): Record<string, unknown> {
  const value = JSON.parse(raw) as unknown;
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new TypeError("Invalid rendezvous frame");
  return value as Record<string, unknown>;
}
